import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/shared/ui/form';
import {
  Select as ShadcnSelect,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
} from '@/shared/ui/select';
import { Input } from '@/shared/ui/input';
import { Switch } from '@/shared/ui/switch';
import { DatePicker } from '@/shared/ui/date-picker';
import { FormFooter } from '@/shared/widgets';
import { FileUpload } from '@/features/collections/components';
import { useTranslation } from '@/shared/i18n';
import { useCreateBanner, useUpdateBanner, useBanner } from '../model';
import { bannerFormSchema, type BannerFormValues } from '../model';
import {
  BannerRedirectType,
  REDIRECT_TYPES_REQUIRING_COLLECTION,
  type BannerData,
} from '../types';

const formatDateForApi = (date?: Date): string | undefined => {
  if (!date) return undefined;
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
};

const parseDateFromBackend = (dateString?: string | null): Date | undefined => {
  if (!dateString) return undefined;
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

export function BannerForm() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { t } = useTranslation('banners');

  const { banner, isLoading: loadingBanner } = useBanner(id, { enabled: isEdit });
  const { createBanner, isLoading: creating } = useCreateBanner();
  const { updateBanner, isLoading: updating } = useUpdateBanner();

  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  const form = useForm<BannerFormValues>({
    resolver: zodResolver(bannerFormSchema),
    defaultValues: {
      description: '',
      redirectType: BannerRedirectType.TEMPLATES_GALLERY,
      redirectId: '',
      displayOrder: 0,
      isActive: false,
      published: false,
      startDate: undefined,
      endDate: undefined,
      minBuildAndroid: null,
      minBuildIos: null,
    },
  });

  const redirectType = form.watch('redirectType');
  const requiresCollection = REDIRECT_TYPES_REQUIRING_COLLECTION.includes(redirectType);

  useEffect(() => {
    if (banner) {
      form.reset({
        description: banner.description,
        redirectType: banner.redirectType ?? BannerRedirectType.TEMPLATES_GALLERY,
        redirectId: banner.redirectId ?? '',
        displayOrder: banner.displayOrder ?? 0,
        isActive: banner.isActive,
        published: banner.published,
        startDate: parseDateFromBackend(banner.startDate),
        endDate: parseDateFromBackend(banner.endDate),
        minBuildAndroid: banner.minBuildAndroid,
        minBuildIos: banner.minBuildIos,
      });
      if (banner.url) setImagePreview(banner.url);
    }
  }, [banner, form]);

  useEffect(() => {
    if (imageFile) {
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result as string);
      reader.readAsDataURL(imageFile);
    }
  }, [imageFile]);

  const isSubmitting = creating || updating;

  const buildData = (values: BannerFormValues): BannerData => ({
    description: values.description,
    redirectType: values.redirectType,
    redirectId: REDIRECT_TYPES_REQUIRING_COLLECTION.includes(values.redirectType)
      ? values.redirectId?.trim() || null
      : null,
    displayOrder: values.displayOrder,
    isActive: values.isActive,
    published: values.published,
    startDate: formatDateForApi(values.startDate) ?? null,
    endDate: formatDateForApi(values.endDate) ?? null,
    minBuildAndroid: values.minBuildAndroid ?? null,
    minBuildIos: values.minBuildIos ?? null,
  });

  const onSubmit = async (values: BannerFormValues) => {
    try {
      if (isEdit && id) {
        await updateBanner(id, {
          data: buildData(values),
          image: imageFile ?? undefined,
        });
        navigate('/mobile/banners');
        return;
      }

      if (!imageFile) {
        form.setError('description', { message: t('imageRequired') });
        return;
      }

      await createBanner({ data: buildData(values), image: imageFile });
      navigate('/mobile/banners');
    } catch (err) {
      console.error('Erro ao salvar banner:', err);
    }
  };

  if (isEdit && loadingBanner) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/mobile/banners')}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <h1 className="text-3xl font-bold tracking-tight">{t('loading')}</h1>
        </div>
        <Card>
          <CardContent className="p-6">
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/mobile/banners')}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isEdit ? t('editBanner') : t('newBanner')}
          </h1>
          <p className="text-muted-foreground">
            {isEdit ? t('updateBannerInfo') : t('fillBannerData')}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card className="bg-white/[0.03] border-white/[0.06]">
            <CardHeader>
              <CardTitle>{t('banner')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('description')} *</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder={t('descriptionPlaceholder')} />
                    </FormControl>
                    <FormDescription>{t('descriptionHelp')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FileUpload
                label={t('image')}
                description={t('imageHelp')}
                preview={imagePreview}
                onFileChange={(file) => {
                  setImageFile(file);
                  if (!file) setImagePreview(null);
                }}
              />

              <FormField
                control={form.control}
                name="redirectType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('redirectType')}</FormLabel>
                    <ShadcnSelect value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder={t('selectRedirectType')} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {Object.values(BannerRedirectType).map((type) => (
                          <SelectItem key={type} value={type}>
                            {type}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </ShadcnSelect>
                    <FormDescription>{t('redirectTypeHelp')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              {requiresCollection && (
                <FormField
                  control={form.control}
                  name="redirectId"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('redirectId')} *</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder={t('redirectIdPlaceholder')} />
                      </FormControl>
                      <FormDescription>{t('redirectIdHelp')}</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="displayOrder"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('order')}</FormLabel>
                    <FormControl>
                      <Input type="number" min={0} {...field} />
                    </FormControl>
                    <FormDescription>{t('orderHelp')}</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="isActive"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">{t('active')}</FormLabel>
                      <FormDescription>{t('activeHelp')}</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="published"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">{t('published')}</FormLabel>
                      <FormDescription>{t('publishedHelp')}</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={field.value ?? false} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />

              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="startDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('startDate')}</FormLabel>
                      <DatePicker
                        date={field.value}
                        onSelect={field.onChange}
                        placeholder={t('selectStartDate')}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="endDate"
                  render={({ field }) => (
                    <FormItem className="flex flex-col">
                      <FormLabel>{t('endDate')}</FormLabel>
                      <DatePicker
                        date={field.value}
                        onSelect={field.onChange}
                        placeholder={t('selectEndDate')}
                      />
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white/[0.03] border-white/[0.06]">
            <CardHeader>
              <CardTitle>{t('minVersionSection')}</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-sm text-muted-foreground">{t('minVersionDescription')}</p>
              <div className="grid gap-6 md:grid-cols-2">
                <FormField
                  control={form.control}
                  name="minBuildAndroid"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('minBuildAndroid')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          placeholder={t('minBuildPlaceholder')}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(e.target.value === '' ? null : Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="minBuildIos"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('minBuildIos')}</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min={1}
                          placeholder={t('minBuildPlaceholder')}
                          value={field.value ?? ''}
                          onChange={(e) =>
                            field.onChange(e.target.value === '' ? null : Number(e.target.value))
                          }
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </CardContent>
          </Card>

          <FormFooter isLoading={isSubmitting}>
            <div className="flex gap-3 justify-between w-full">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/mobile/banners')}
                disabled={isSubmitting}
              >
                {t('cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? t('saving') : isEdit ? t('saveChanges') : t('createBanner')}
              </Button>
            </div>
          </FormFooter>
        </form>
      </Form>
    </div>
  );
}
